<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div>
        <div class="flex items-center gap-2.5">
          <h2 class="text-navy-800 text-base font-semibold">
            {{ member?.name || "Member" }}
          </h2>
          <span
            v-if="member"
            :class="[
              'rounded px-2 py-0.5 text-[11px] font-medium',
              roleClasses(member.role),
            ]"
          >
            {{ member.role }}
          </span>
        </div>
        <p class="text-[13px] text-neutral-500">
          Manage member role and access
        </p>
      </div>
    </template>

    <template v-if="member">
      <!-- Member details -->
      <div class="grid grid-cols-2 gap-x-5 gap-y-3">
        <div>
          <div class="mb-0.5 text-[11px] text-neutral-400">Name</div>
          <div class="text-navy-800 text-[13px]">{{ member.name }}</div>
        </div>
        <div>
          <div class="mb-0.5 text-[11px] text-neutral-400">Email</div>
          <div class="text-navy-800 text-[13px]">{{ member.email }}</div>
        </div>
      </div>

      <!-- Role section -->
      <div class="-mx-6 mt-5 border-t border-neutral-200 px-6 pt-4">
        <h3
          class="mb-3 text-[11px] font-bold tracking-[0.06em] text-neutral-400 uppercase"
        >
          Role
        </h3>
        <AppSelect
          v-model="roleValue"
          :options="roleOptions"
          placeholder="Select a role"
        />
        <p class="mt-1 text-xs text-neutral-400">
          {{ roleDescription }}
        </p>
      </div>

      <!-- Officer designation -->
      <div class="-mx-6 mt-5 border-t border-neutral-200 px-6 pt-4">
        <h3
          class="mb-3 text-[11px] font-bold tracking-[0.06em] text-neutral-400 uppercase"
        >
          Officer Designation
        </h3>
        <label class="flex cursor-pointer items-center justify-between">
          <div>
            <p class="text-navy-800 text-[13px] font-medium">
              Assignable as Officer
            </p>
            <p class="mt-0.5 text-xs text-neutral-400">
              Allow this member to be assigned as the officer on orders
            </p>
          </div>
          <div class="relative h-6 w-11 shrink-0">
            <input
              v-model="officerValue"
              type="checkbox"
              class="peer sr-only"
            />
            <div
              class="bg-navy-200 h-6 w-11 rounded-full transition-colors peer-checked:bg-green-600"
            />
            <div
              class="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
            />
          </div>
        </label>
      </div>

      <!-- MFA section -->
      <div class="-mx-6 mt-5 border-t border-neutral-200 px-6 pt-4">
        <h3
          class="mb-3 text-[11px] font-bold tracking-[0.06em] text-neutral-400 uppercase"
        >
          Two-Factor Authentication
        </h3>
        <div class="flex items-center justify-between">
          <div>
            <div
              class="flex items-center gap-1.5 text-[13px] font-medium"
              :class="member.mfaEnabled ? 'text-green-600' : 'text-neutral-400'"
            >
              <span
                class="inline-block h-1.5 w-1.5 rounded-full"
                :class="member.mfaEnabled ? 'bg-green-600' : 'bg-neutral-300'"
              />
              {{ member.mfaEnabled ? "Enabled" : "Not enabled" }}
            </div>
          </div>
          <AppButton
            v-if="member.mfaEnabled"
            variant="danger"
            size="sm"
            :loading="resettingMfa"
            @click="confirmResetMfa"
          >
            Reset MFA
          </AppButton>
        </div>
      </div>
    </template>

    <template #footer>
      <div v-if="member" class="flex justify-between">
        <AppButton variant="danger" size="sm" @click="confirmRemove">
          Remove Member
        </AppButton>
        <AppButton
          :loading="saving"
          :disabled="!hasChanges"
          @click="saveChanges"
        >
          Save Changes
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref, watch } from "vue"

import type { MemberType } from "../../pages/Company/types"
import { apolloClient } from "../../queries/client"
import DESTROY_MEMBER from "../../queries/member/destroy.gql"
import RESET_MFA from "../../queries/member/reset_mfa.gql"
import UPDATE_MEMBER from "../../queries/member/update.gql"
import AppButton from "../ui/AppButton.vue"
import AppSelect from "../ui/AppSelect.vue"

interface Props {
  member: MemberType | null
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  updated: [member: MemberType]
  removed: []
}>()

const toast = useToast()
const confirm = useConfirm()
const roleValue = ref("")
const officerValue = ref(false)
const saving = ref(false)
const resettingMfa = ref(false)

const roleOptions = [
  { label: "Admin", value: "ADMIN" },
  { label: "Contributor", value: "CONTRIBUTOR" },
  { label: "Viewer", value: "VIEWER" },
]

const roleDescription = computed(() => {
  if (roleValue.value === "ADMIN")
    return "Full access — manage members, agents, and company settings."
  if (roleValue.value === "CONTRIBUTOR")
    return "Can create and manage orders, but cannot manage company settings."
  if (roleValue.value === "VIEWER")
    return "Read-only access to orders and company information."
  return ""
})

const hasChanges = computed(() => {
  if (!props.member) return false
  return (
    roleValue.value !== props.member.role.toUpperCase() ||
    officerValue.value !== props.member.officer
  )
})

watch(
  () => props.member,
  (m) => {
    if (m) {
      roleValue.value = m.role.toUpperCase()
      officerValue.value = m.officer
    }
  },
  { immediate: true },
)

const roleClasses = (role: string) => {
  const r = role.toUpperCase()
  if (r === "ADMIN") return "bg-blue-100 text-blue-800"
  if (r === "CONTRIBUTOR") return "bg-violet-100 text-violet-700"
  return "bg-neutral-100 text-neutral-500"
}

const saveChanges = async () => {
  if (!props.member || !hasChanges.value) return

  saving.value = true
  try {
    const result = await apolloClient.mutate({
      mutation: UPDATE_MEMBER,
      variables: {
        memberId: props.member.id,
        role: roleValue.value,
        name: props.member.name,
        email: props.member.email,
        officer: officerValue.value,
      },
    })
    const data = result.data?.updateMember
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "Saved",
      detail: "Member role updated",
      life: 3000,
    })

    emit("updated", data.member)
    visible.value = false
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to update member",
      life: 4000,
    })
  } finally {
    saving.value = false
  }
}

const confirmRemove = () => {
  if (!props.member) return

  confirm.require({
    message: `This will remove ${props.member.name} from the company. Are you sure?`,
    header: "Remove Member",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void removeMember()
    },
  })
}

const confirmResetMfa = () => {
  if (!props.member) return

  confirm.require({
    message: `This will disable MFA for ${props.member.name}. They will need to set it up again.`,
    header: "Reset MFA",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void resetMfa()
    },
  })
}

const resetMfa = async () => {
  if (!props.member) return

  resettingMfa.value = true
  try {
    const result = await apolloClient.mutate({
      mutation: RESET_MFA,
      variables: { memberId: props.member.id },
    })
    const data = result.data?.resetMfa
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "MFA Reset",
      detail: `MFA has been disabled for ${props.member.name}`,
      life: 3000,
    })

    emit("updated", { ...props.member, mfaEnabled: false })
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to reset MFA",
      life: 4000,
    })
  } finally {
    resettingMfa.value = false
  }
}

const removeMember = async () => {
  if (!props.member) return

  try {
    const result = await apolloClient.mutate({
      mutation: DESTROY_MEMBER,
      variables: { memberId: props.member.id },
    })
    const data = result.data?.destroyMember
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "Removed",
      detail: "Member removed from company",
      life: 3000,
    })

    emit("removed")
    visible.value = false
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to remove member",
      life: 4000,
    })
  }
}
</script>
